import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MoreVertical as FiMoreVertical,
    Eye as FiEye,
    Send as FiSend,
    MapPin as FiMapPin,
    Calendar as FiCalendar,
    ArrowRight as FiArrowRight,
    Info as FiInfo,
    Share2 as FiShare
} from 'lucide-react';
import { cn } from '../../../lib/utils';

const JobCard = ({ job, onViewDetails, onApply, onViewCompany, isApplied, appStatus, viewMode = 'grid' }) => {
    const [showMenu, setShowMenu] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [copied, setCopied] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    useEffect(() => {
        const h = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);

    // On mobile, always use column layout regardless of viewMode
    const isRow = viewMode === 'list' && !isMobile;

    const handleShare = (e) => {
        e.stopPropagation();
        const url = `${window.location.origin}/job-portal/opportunities?job=${job.id}`;
        const title = `${job.title} at ${job.companyName}`;
        const text = `Check out this opportunity: ${job.title} at ${job.companyName}`;
        if (navigator.share) {
            navigator.share({ title, text, url }).catch(() => { });
        } else {
            navigator.clipboard.writeText(url).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    };

    const MenuBar = () => (
        <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -5 }}
            className="absolute top-full right-0 mt-2 border border-border rounded-xl p-1.5 min-w-[180px] shadow-2xl z-[10100] backdrop-blur-xl"
            style={{ background: 'var(--bg-card-solid, #1c1c21)' }}
        >
            <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-bold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={(e) => { e.stopPropagation(); setShowMenu(false); onViewCompany(job.companyId); }}>
                <FiInfo size={15} /> View Company
            </button>
            <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-bold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={(e) => { e.stopPropagation(); setShowMenu(false); onViewDetails(job); }}>
                <FiEye size={15} /> View Details
            </button>
            <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-bold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={(e) => { setShowMenu(false); handleShare(e); }}>
                <FiShare size={15} /> Share Job
            </button>
            {!isApplied && (
                <button className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-bold text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" onClick={(e) => { e.stopPropagation(); setShowMenu(false); onApply(job); }}>
                    <FiSend size={15} /> Apply Now
                </button>
            )}
        </motion.div>
    );

    return (
        <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -3 }}
            onClick={() => onViewDetails(job)}
            className={cn(
                "p-5 bg-card border border-border rounded-[1.5rem] transition-all duration-300 relative cursor-pointer flex hover:bg-accent/40 hover:border-primary hover:shadow-2xl hover:shadow-primary/5 group",
                viewMode === 'list' && !isMobile ? "flex-row items-center gap-6" : "flex-col gap-3.5",
                showMenu && "z-[1001]"
            )}
        >
            {/* Header: Company logo + title */}
            {/* Header: Company logo + title */}
            <div className={cn(
                "flex justify-between items-center shrink-0",
                isRow ? "w-1/4 min-w-[220px]" : "w-full"
            )}>
                <div className="flex gap-3 items-center flex-1 min-w-0">
                    <div className={cn(
                        "rounded-xl shrink-0 flex items-center justify-center text-lg font-black bg-gradient-to-br from-primary/20 to-secondary/20 text-primary border border-primary/10",
                        isRow ? "w-10 h-10" : "w-12 h-12"
                    )}>
                        {job.companyName?.charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className={cn(
                            "m-0 font-black truncate text-foreground tracking-tight",
                            isRow ? "text-[14px]" : "text-[16px]"
                        )}>{job.title}</h3>
                        <div className="flex items-center gap-1.5">
                            <p className="m-0 text-xs text-muted-foreground font-bold truncate max-w-[120px]">{job.companyName}</p>
                            <button
                                onClick={(e) => { e.stopPropagation(); onViewCompany(job.companyId); }}
                                className="bg-none border-none p-0 flex text-primary cursor-pointer opacity-70 hover:opacity-100 transition-opacity shrink-0"
                                title="View Company"
                            >
                                <FiInfo size={12} />
                            </button>
                        </div>
                    </div>
                </div>

                {/* 3-dot menu (only in Card/Grid view) */}
                {!isRow && (
                    <div className="relative" ref={menuRef}>
                        <button
                            className={cn(
                                "w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-all",
                                showMenu && "bg-accent text-foreground"
                            )}
                            onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                        >
                            <FiMoreVertical size={16} />
                        </button>
                        <AnimatePresence>
                            {showMenu && (
                                <MenuBar />
                            )}
                        </AnimatePresence>
                    </div>
                )}
            </div>

            {/* Tags (Details Column) */}
            <div className={cn(
                "flex flex-wrap gap-2 items-center min-w-0",
                isRow ? "flex-1 justify-center" : "justify-start"
            )}>
                {job.location && (
                    <span className="flex items-center gap-1 text-[10px] text-muted-foreground bg-accent/50 px-2.5 py-1 rounded-full font-bold uppercase tracking-wider shrink-0 border border-border/50">
                        <FiMapPin size={10} /> {job.location}
                    </span>
                )}
                {job.type && (
                    <span className={cn(
                        "text-[10px] px-2.5 py-1 rounded-full font-black uppercase tracking-wider shrink-0 border",
                        job.type.includes('Walk-in') ? "text-orange-500 bg-orange-500/10 border-orange-500/20" :
                            job.type.includes('Assessment') ? "text-purple-500 bg-purple-500/10 border-purple-500/20" :
                                "text-primary bg-primary/10 border-primary/20"
                    )}>
                        {job.type}
                    </span>
                )}
                {(job.type?.includes('Walk-in') || job.type?.includes('Assessment')) && job.dateTime && (
                    <span className="flex items-center gap-1 text-[10px] text-destructive bg-destructive/10 px-2.5 py-1 rounded-full font-black uppercase tracking-wider shrink-0 border border-destructive/20">
                        <FiCalendar size={10} /> {new Date(job.dateTime).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                )}
                {job.workMode && job.workMode !== 'All' && (
                    <span className="text-[10px] text-secondary bg-secondary/10 px-2.5 py-1 rounded-full font-black uppercase tracking-wider shrink-0 border border-secondary/20">
                        {job.workMode}
                    </span>
                )}
                {job.stipend && (
                    <span className="flex items-center gap-1 text-[10px] text-emerald-500 bg-emerald-500/10 px-2.5 py-1 rounded-full font-black uppercase tracking-wider shrink-0 border border-emerald-500/20">
                        <span className="text-xs font-black">₹</span> {job.stipend}
                    </span>
                )}
                {/* Paid / Unpaid badge */}
                {job.isPaid === false && (
                    <span className="text-[10px] text-amber-500 bg-amber-500/10 px-2.5 py-1 rounded-full font-black uppercase tracking-wider shrink-0 border border-amber-500/20">
                        Unpaid
                    </span>
                )}
            </div>

            {/* Actions Column */}
            <div className={cn(
                "flex items-center shrink-0 transition-all duration-300",
                isRow ? "w-auto gap-6" : "w-full justify-between mt-auto gap-3"
            )}>
                <span className="text-[10px] text-muted-foreground font-black uppercase tracking-widest whitespace-nowrap">
                    {job.createdAt?.toDate ? job.createdAt.toDate().toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Recently'}
                </span>

                <div className="flex items-center gap-3">
                    {/* Share button — always visible */}
                    <div className="relative">
                        <button
                            title="Share job link"
                            onClick={handleShare}
                            className="w-8 h-8 rounded-lg flex items-center justify-center bg-accent/50 border border-border text-muted-foreground hover:text-primary hover:border-primary/40 transition-all"
                        >
                            <FiShare size={14} />
                        </button>
                        <AnimatePresence>
                            {copied && (
                                <motion.div
                                    initial={{ opacity: 0, y: 4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 whitespace-nowrap bg-foreground text-background text-[10px] font-black px-2 py-1 rounded-lg pointer-events-none"
                                >
                                    Link copied!
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* 3-dot menu (only in List View) */}
                    {isRow && (
                        <div className="relative" ref={menuRef}>
                            <button
                                className={cn(
                                    "w-9 h-9 rounded-lg flex items-center justify-center bg-accent/50 border border-border text-muted-foreground hover:text-foreground transition-all",
                                    showMenu && "bg-accent text-foreground"
                                )}
                                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                            >
                                <FiMoreVertical size={16} />
                            </button>
                            <AnimatePresence>
                                {showMenu && (
                                    <MenuBar />
                                )}
                            </AnimatePresence>
                        </div>
                    )}

                    {isApplied ? (
                        <span className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap border" style={{ backgroundColor: appStatus?.bg, color: appStatus?.color, borderColor: appStatus?.color + '40' }}>
                            {appStatus?.label}
                        </span>
                    ) : (
                        <span className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-xs font-black uppercase tracking-widest flex items-center gap-2 shrink-0 whitespace-nowrap shadow-lg shadow-primary/20 group-hover:bg-primary/90 transition-all">
                            View Details <FiArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default JobCard;
